import { Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import { MessengerService } from '../../services/messenger.service';
import {
  SetActiveMessageAction,
  GetMessagesAction,
  NewMessageReceivedAction,
  GetChatMessagesAction,
  SetChatLoadingAction,
  BackAction,
  SelectedFilterAction,
  SendMessageAction,
  IsSendingMessageAction,
  CheckMessagesAction,
  NewMessageAddedAction,
  MessagesCheckedAction,
  ChangeStatusReceivedAction,
  ChangeTrackingReceivedAction,
  SendGlobalMessageAction,
  ResetStateAction,
  MessengerReconnectionAction,
  IsGlobalMessageOpenAction,
} from './messenger.action';
import {
  ChatFilterType,
  IChatFilter,
  IChatMessage,
  IChatSender,
  IGlobalMessage,
  IMachineMessage,
  IMessengerStateModel,
} from '../../interfaces/messenger';
import { CacheService } from 'projects/common/src/app/services';
import { forkJoin } from 'rxjs';
import * as moment from 'moment';
import { Utility } from 'projects/common/src/app/classes/utility';

@State<IMessengerStateModel>({
  name: 'chat',
  defaults: {
    messages: [],
    unreadMessages: [],
    activeMessage: null,
    isChatLoading: false,
    messengerFilter: { isActive: false, search: '' },
    messageUpdated: null,
    newTracking: null,
    isSendingMessage: false,
    isCheckingMessages: false,
    messagesChecked: false,
    isGlobalMessageOpen: false,
    sidebarNotification: 0,
  },
})
@Injectable()
export class MessengerState {
  private pageSize = 12;
  private machineMessagesStorage: IMachineMessage[] = [];
  private chatMessageStorage: IChatMessage[] = [];
  private filterStorage: IChatFilter;

  constructor(private _messengerService: MessengerService, private _cacheService: CacheService) {
    this.filterStorage = this._messengerService.getInitialFilterState();
  }

  @Action(GetMessagesAction)
  getMachinesMessages(ctx: StateContext<IMessengerStateModel>) {
    this._messengerService.getAvailableMachines().subscribe({
      next: (machineMessages) => {
        this.machineMessagesStorage = this._messengerService.handleMachineMessages(machineMessages);
        const unreadMessages = this._messengerService.getUnreadMessages(this.machineMessagesStorage);

        ctx.patchState({
          messages: this.machineMessagesStorage,
          unreadMessages,
          sidebarNotification: this._messengerService.getUnreadNotificationSidebar(unreadMessages),
        });
      },
      error: () => {},
    });
  }

  @Action(SetActiveMessageAction)
  setActiveMessage(ctx: StateContext<IMessengerStateModel>, action: SetActiveMessageAction) {
    const activeMachine = action.payload;

    // reset chat
    this.chatMessageStorage = [];

    // reset unread messages
    this.machineMessagesStorage = this.machineMessagesStorage.map((machineMessage) => {
      if (machineMessage.machineId === activeMachine.machineId) {
        return {
          ...machineMessage,
          unreadMessageQty: 0,
        };
      }

      return { ...machineMessage };
    });

    const unreadMessages = this._messengerService.getUnreadMessages(this.machineMessagesStorage);

    ctx.patchState({
      activeMessage: { ...activeMachine },
      unreadMessages,
      sidebarNotification: this._messengerService.getUnreadNotificationSidebar(unreadMessages),
    });

    return ctx.dispatch(new GetChatMessagesAction(0));
  }

  @Action(SetChatLoadingAction)
  setChatLoading(ctx: StateContext<IMessengerStateModel>, action: SetChatLoadingAction) {
    ctx.patchState({
      isChatLoading: action.payload,
    });
  }

  @Action(IsSendingMessageAction)
  isSendingMessage(ctx: StateContext<IMessengerStateModel>, action: IsSendingMessageAction) {
    ctx.patchState({
      isSendingMessage: action.payload,
    });
  }

  @Action(GetChatMessagesAction)
  getChatMessages(ctx: StateContext<IMessengerStateModel>, action: GetChatMessagesAction) {
    ctx.dispatch(new SetChatLoadingAction(true));

    const { activeMessage } = ctx.getState();
    const page = action.payload;

    this._messengerService.getMessagesByMachineId(activeMessage.machineId, page, this.pageSize).subscribe({
      next: (res) => {
        if (res.data.length > 0) {
          const tempMessageList = [];
          res.data.forEach((message) => {
            const foundMesj = this.chatMessageStorage.find((value) => value.messageId === message.messageId);
            if (!foundMesj) {
              tempMessageList.push(message);
            }
          });

          // New incoming messages are concatenated with existing ones, the spMessagesGetPagingByMachineId is ordering them by creationDate
          this.chatMessageStorage = tempMessageList.concat(this.chatMessageStorage);
          this.chatMessageStorage = this._messengerService.checkDelimiters(this.chatMessageStorage);
        }

        ctx.patchState({
          isChatLoading: false,
          activeMessage: {
            ...activeMessage,
            preventScroll: false,
            chatMessages: {
              totalCount: res.totalCount,
              data: this.chatMessageStorage,
            },
          },
        });

        ctx.dispatch(new CheckMessagesAction());
      },
    });
  }

  @Action(CheckMessagesAction)
  checkMessages(ctx: StateContext<IMessengerStateModel>) {
    const unchekedMessages = this.chatMessageStorage.filter((message) => !message.readAt && message.senderRole !== 'SERVER');
    const { activeMessage } = ctx.getState();

    if (!unchekedMessages.length) {
      ctx.dispatch(new MessagesCheckedAction(true));
      return;
    }

    const requests = unchekedMessages.map((message) => this._messengerService.checkMessage(message));

    ctx.patchState({ isCheckingMessages: true });

    forkJoin(requests).subscribe(
      () => {
        // console.log('Todas las solicitudes se completaron', results);
        const currentDate = moment().format('YYYY-MM-DDTHH:mm:ss.SSSSSSSZ');

        // mark messages as read
        this.chatMessageStorage = [...this.chatMessageStorage].map((message) => {
          if (unchekedMessages.find((unreadMessage) => message.messageId === unreadMessage.messageId)) {
            return {
              ...message,
              readAt: currentDate,
            };
          }
          return { ...message };
        });

        // mark machine messages as read
        this.machineMessagesStorage = this.machineMessagesStorage.map((machineMessage) => {
          if (machineMessage.machineId === activeMessage.machineId) {
            return {
              ...machineMessage,
              unreadMessageQty: 0,
            };
          }

          return { ...machineMessage };
        });

        const filteredList = this._messengerService.filterMessages([...this.machineMessagesStorage], this.filterStorage);

        ctx.patchState({
          isCheckingMessages: false,
          messages: filteredList,
          activeMessage: {
            ...activeMessage,
            chatMessages: {
              totalCount: activeMessage.chatMessages.totalCount,
              data: this.chatMessageStorage,
            },
          },
        });

        ctx.dispatch(new MessagesCheckedAction(true));
      },
      (error) => {
        console.error(error);
        ctx.patchState({
          isCheckingMessages: false,
          messagesChecked: false,
        });
      },
    );
  }

  @Action(NewMessageReceivedAction)
  onNewMessageReceived(ctx: StateContext<IMessengerStateModel>, action: NewMessageReceivedAction) {
    console.log('NewMessageReceived', action.payload);
    const incomingMessage = action.payload;
    const { activeMessage } = ctx.getState();

    if (activeMessage?.machineId === incomingMessage.machineId) {
      const currChatMsg = activeMessage.chatMessages;
      const messageFound = activeMessage.chatMessages?.data.find((m) => m.messageId === incomingMessage.messageId);

      if (incomingMessage.messageType.toLowerCase() === 'new') {
        if (!messageFound) {
          this.chatMessageStorage = [...this.chatMessageStorage, incomingMessage];

          ctx.patchState({
            activeMessage: {
              ...activeMessage,
              chatMessages: {
                totalCount: currChatMsg.totalCount + 1,
                data: this.chatMessageStorage,
              },
            },
          });
        }
      } else if (incomingMessage.messageType.toLowerCase() === 'update') {
        // handle read tickets
        this.chatMessageStorage = this.chatMessageStorage.map((message) => {
          if (message.messageId === incomingMessage.messageId) {
            return {
              ...message,
              createdAt: incomingMessage.createdAt ? incomingMessage.createdAt : messageFound.createdAt,
              syncAt: incomingMessage.syncAt ? incomingMessage.syncAt : messageFound.syncAt,
              readAt: incomingMessage.readAt ? incomingMessage.readAt : messageFound.readAt,
            };
          }
          return { ...message };
        });

        // update last reading date
        this.machineMessagesStorage = this.machineMessagesStorage.map((machineMessage) => {
          if (machineMessage.machineId === incomingMessage.machineId) {
            return {
              ...machineMessage,
              readAt: incomingMessage.readAt,
            };
          }

          return { ...machineMessage };
        });

        ctx.patchState({
          activeMessage: {
            ...activeMessage,
            readAt: incomingMessage.readAt ?? activeMessage.readAt,
            chatMessages: {
              totalCount: currChatMsg.totalCount,
              data: this.chatMessageStorage,
            },
          },
        });
      }
    }

    if (incomingMessage.messageType.toLowerCase() === 'new') {
      ctx.dispatch(new NewMessageAddedAction(incomingMessage));
    }

    if (incomingMessage.messageType.toLowerCase() === 'update') {
      ctx.patchState({
        messageUpdated: incomingMessage,
      });
    }
  }

  @Action(ChangeStatusReceivedAction)
  onChangeStatusReceived(ctx: StateContext<IMessengerStateModel>, action: ChangeStatusReceivedAction) {
    console.log('NewStatusReceived', action.payload);
    const newStatus = action.payload;
    let { activeMessage } = ctx.getState();

    this.machineMessagesStorage = this.machineMessagesStorage.map((machineMessage) => {
      if (machineMessage.machineId === newStatus.machine?.id) {
        return {
          ...machineMessage,
          statusFamilyName: newStatus.statusParent?.statusImagePrefix,
          statusColor: newStatus.status?.statusFamilyColor || '#ffcf39',
          statusName: newStatus.status?.name || 'Sin Estado',
          parentStatusImagePrefix: newStatus.statusParent?.statusImagePrefix,
          imageKey: Utility.getSideMachineIconKey(newStatus.statusParent?.statusImagePrefix, machineMessage.machineIcon),
          parentStatusSecondColor: newStatus.status?.secondColor || '#433E3E',
          parentStatusThirdColor: newStatus.status?.thirdColor || '#ffffff',
          isConnected: newStatus.machine?.isConnected,
        };
      }

      return { ...machineMessage };
    });

    if (activeMessage?.machineId === newStatus.machine?.id) {
      this.chatMessageStorage = [...this.chatMessageStorage].map((message) => {
        return {
          ...message,
          statusColor: newStatus.status?.statusFamilyColor || '#ffcf39',
        };
      });

      activeMessage = {
        ...activeMessage,
        statusFamilyName: newStatus.statusParent?.statusImagePrefix,
        statusColor: newStatus.status?.statusFamilyColor || '#ffcf39',
        statusName: newStatus.status?.name || 'Sin Estado',
        parentStatusImagePrefix: newStatus.statusParent?.statusImagePrefix,
        imageKey: Utility.getSideMachineIconKey(newStatus.statusParent?.statusImagePrefix, activeMessage.machineIcon),
        parentStatusSecondColor: newStatus.status?.secondColor || '#433E3E',
        parentStatusThirdColor: newStatus.status?.thirdColor || '#ffffff',
        isConnected: newStatus.machine?.isConnected,
        preventScroll: true,
        chatMessages: {
          totalCount: activeMessage.chatMessages.totalCount,
          data: this.chatMessageStorage,
        },
      };
    }

    const filteredList = this._messengerService.filterMessages([...this.machineMessagesStorage], this.filterStorage);
    const unreadMessages = this._messengerService.getUnreadMessages(filteredList);

    ctx.patchState({
      messages: filteredList,
      unreadMessages,
      activeMessage,
      sidebarNotification: this._messengerService.getUnreadNotificationSidebar(
        this._messengerService.getUnreadMessages(this.machineMessagesStorage),
      ),
    });
  }

  @Action(ChangeTrackingReceivedAction)
  onChangeTrackingReceived(ctx: StateContext<IMessengerStateModel>, action: ChangeTrackingReceivedAction) {
    console.log('ChangeTrackingReceived', action.payload);
    const newTracking = action.payload;

    this.machineMessagesStorage = this.machineMessagesStorage.map((machineMessage) => {
      if (machineMessage.machineId === newTracking.machineId) {
        return {
          ...machineMessage,
          zoneId: newTracking.zoneId,
          zoneName: newTracking.zoneName,
        };
      }

      return { ...machineMessage };
    });

    ctx.patchState({
      newTracking,
    });
  }

  @Action(BackAction)
  onBack(ctx: StateContext<IMessengerStateModel>) {
    this.chatMessageStorage = [];

    // rearrange and filter the machine list
    this.machineMessagesStorage = this._messengerService.handleMachineMessages(this.machineMessagesStorage);
    const filteredList = this._messengerService.filterMessages([...this.machineMessagesStorage], this.filterStorage);
    const unreadMessages = this._messengerService.getUnreadMessages(filteredList);

    ctx.patchState({
      activeMessage: null,
      messages: filteredList,
      unreadMessages,
      sidebarNotification: this._messengerService.getUnreadNotificationSidebar(
        this._messengerService.getUnreadMessages(this.machineMessagesStorage),
      ),
    });
  }

  @Action(SelectedFilterAction)
  selectedFilter(ctx: StateContext<IMessengerStateModel>, action: SelectedFilterAction) {
    const { payload } = action;

    switch (payload.type) {
      case ChatFilterType.ZONE:
        this.filterStorage.zoneParkIds = payload.value;
        break;
      case ChatFilterType.MACHINE_TYPE:
        this.filterStorage.machineTypeId = payload.value;
        break;
      case ChatFilterType.STATUS_MACHINE:
        this.filterStorage.statusNames = payload.value;
        break;
      case ChatFilterType.SEARCH:
        this.filterStorage.search = payload.value;
        break;
      default:
        break;
    }

    const filteredList = this._messengerService.filterMessages([...this.machineMessagesStorage], this.filterStorage);
    const unreadMessages = this._messengerService.getUnreadMessages(filteredList);
    const isFiltered = !!(
      this.filterStorage.zoneParkIds?.length ||
      this.filterStorage.machineTypeId ||
      this.filterStorage.statusNames?.length ||
      this.filterStorage.search
    );

    ctx.patchState({
      messages: filteredList,
      messengerFilter: { isActive: isFiltered, search: this.filterStorage.search },
      unreadMessages,
    });
  }

  @Action(SendMessageAction)
  sendMessage(ctx: StateContext<IMessengerStateModel>, action: SendMessageAction) {
    const { user } = this._cacheService.local('session');
    const { position, firstName, lastName } = user;
    const company = user.company?.name ?? '';

    // const currentDay: Moment = getMoment(new Date());

    const activeMsg = ctx.getState().activeMessage;

    const messageSender: IChatSender = {
      senderName: `${position} - (${company}): ${firstName} ${lastName}`,
      machineId: activeMsg.machineId,
      senderRole: 'SERVER',
      text: action.payload.trim(),
    };

    ctx.dispatch(new IsSendingMessageAction(true));

    this._messengerService.sendMessage(messageSender).subscribe({
      next: (data) => {
        this.chatMessageStorage = [...this.chatMessageStorage, data];

        this.machineMessagesStorage = this.machineMessagesStorage.map((machineMessage) => {
          if (machineMessage.machineId === activeMsg.machineId) {
            return {
              ...machineMessage,
              body: data.text,
              createdAt: data.createdAt,
            };
          }

          return { ...machineMessage };
        });

        ctx.patchState({
          activeMessage: {
            ...activeMsg,
            chatMessages: {
              totalCount: activeMsg.chatMessages.totalCount + 1,
              data: this.chatMessageStorage,
            },
          },
          isSendingMessage: false,
        });
      },
      error: (err) => {
        console.log(err);
        ctx.dispatch(new IsSendingMessageAction(false));
      },
    });
  }

  @Action(NewMessageAddedAction)
  newMessageAdded(ctx: StateContext<IMessengerStateModel>, action: NewMessageAddedAction) {
    const newMessage = action.payload;
    const { activeMessage } = ctx.getState();

    this.machineMessagesStorage = this.machineMessagesStorage.map((machineMessage) => {
      if (machineMessage.machineId === newMessage.machineId) {
        let qty = machineMessage.unreadMessageQty;

        if (activeMessage?.machineId !== newMessage.machineId && newMessage.senderRole === 'REMOTE') {
          qty = machineMessage.unreadMessageQty ? machineMessage.unreadMessageQty + 1 : 1;
          this._messengerService.playNewMessageSound();
        }

        return {
          ...machineMessage,
          body: newMessage.text,
          unreadMessageQty: qty,
          createdAt: newMessage.createdAt,
        };
      }

      return { ...machineMessage };
    });

    this.machineMessagesStorage = this._messengerService.handleMachineMessages(this.machineMessagesStorage);
    const filteredList = this._messengerService.filterMessages([...this.machineMessagesStorage], this.filterStorage);
    const unreadMessages = this._messengerService.getUnreadMessages(filteredList);

    ctx.patchState({
      messages: filteredList,
      unreadMessages,
      sidebarNotification: this._messengerService.getUnreadNotificationSidebar(
        this._messengerService.getUnreadMessages(this.machineMessagesStorage),
      ),
    });
  }

  @Action(MessagesCheckedAction)
  messagesChecked(ctx: StateContext<IMessengerStateModel>, action: MessagesCheckedAction) {
    ctx.patchState({
      messagesChecked: action.payload,
    });
  }

  @Action(SendGlobalMessageAction)
  sendGlobalMessage(ctx: StateContext<IMessengerStateModel>, action: SendGlobalMessageAction) {
    ctx.dispatch(new IsSendingMessageAction(true));

    const filteredList = this._messengerService.filterMessages([...this.machineMessagesStorage], this.filterStorage);

    const listIds = filteredList.map((v) => v.machineId);

    const globalMessage: IGlobalMessage = {
      senderRole: 'SERVER',
      text: action.payload,
      filterMachineId: listIds.join('|'),
    };

    this._messengerService.sendGlobalMessage(globalMessage).subscribe({
      next: () => {
        ctx.dispatch(new IsSendingMessageAction(false));
      },
      error: (err) => {
        console.log(err);
        ctx.dispatch(new IsSendingMessageAction(false));
      },
    });
  }

  @Action(ResetStateAction)
  resetStateAction(ctx: StateContext<IMessengerStateModel>) {
    this.chatMessageStorage = [];
    this.filterStorage = this._messengerService.getInitialFilterState();

    ctx.patchState({
      activeMessage: null,
    });
  }

  @Action(MessengerReconnectionAction)
  reconnectAction(ctx: StateContext<IMessengerStateModel>) {
    let { activeMessage } = ctx.getState();

    this._messengerService.getAvailableMachines().subscribe({
      next: (machineMessages) => {
        this.machineMessagesStorage = this._messengerService.handleMachineMessages(machineMessages);
        const filteredList = this._messengerService.filterMessages([...this.machineMessagesStorage], this.filterStorage);
        const unreadMessages = this._messengerService.getUnreadMessages(filteredList);

        // This piece will update the status of the current machine if the signalR loses connection
        if (activeMessage) {
          const found = machineMessages.find((message) => message.machineId === activeMessage.machineId);
          if (found) {
            activeMessage = {
              ...activeMessage,
              statusName: found.statusName ?? 'Sin Estado',
              statusColor: found.statusColor ?? '#ffcf39',
              parentStatusSecondColor: found.parentStatusSecondColor || '#433E3E',
              parentStatusThirdColor: found.parentStatusThirdColor || '#ffffff',
            };
          }
        }

        ctx.patchState({
          messages: filteredList,
          unreadMessages,
          activeMessage,
          sidebarNotification: this._messengerService.getUnreadNotificationSidebar(
            this._messengerService.getUnreadMessages(this.machineMessagesStorage),
          ),
        });

        if (activeMessage) {
          ctx.dispatch(new GetChatMessagesAction(0));
        }
      },
      error: () => {
        if (activeMessage) {
          ctx.dispatch(new GetChatMessagesAction(0));
        }
      },
    });
  }

  @Action(IsGlobalMessageOpenAction)
  isGlobalMessageOpen(ctx: StateContext<IMessengerStateModel>, action: IsGlobalMessageOpenAction) {
    ctx.patchState({
      isGlobalMessageOpen: action.payload,
    });
  }
}
